import { useTranslation } from 'react-i18next';
import React from 'react';
import { AxiosError } from 'axios';
import { QueryObserverResult, RefetchOptions } from 'react-query';
import { useGetRequestSummary } from '../../../../api-refactored/group-special-consideration/useGetRequestSummary';
import { FileProcessingState, FileUploadState } from '../../../../models/File';
import { usePutStartFileProcessing } from '../../../../api-refactored/group-special-consideration/usePutStartFileProcessing';
import {
  PutStopFileProcessingRequest,
  usePutStopFileProcessing,
} from '../../../../api-refactored/group-special-consideration/usePutStopFileProcessing';
import {
  PutReUploadExcelFile,
  usePutReUploadExcelFile,
} from '../../../../api-refactored/group-special-consideration/usePutReUploadExcelFile';
import { usePostUploadPresignedUrl } from '../../../../api-refactored/group-special-consideration/usePostUploadPresignedUrl';
import { uploadFile } from '../../../../utils/FileUtils';
import { usePutUploadToS3 } from '../../../../api-refactored/s3-upload/usePutUploadFileToS3';
import { useGetDocumentUploadStatus } from '../../../../api-refactored/document/useGetDocumentUploadStatus';
import { useGetDownloadErrorFile } from '../../../../api-refactored/group-special-consideration/useGetDownloadErrorFile';
import { UseCustomMutationReturn } from '../../../../api-refactored/utils/useCustomMutation';
import {
  PostUploadPresignedUrlRequest,
  PresignedUrlResponse,
} from '../../../../models/PresignedUrl';
import { DownloadFileResponse, GenericErrorResponse } from '../../../../api-refactored/typings';

interface UploadSectionApisInput {
  applicationUuid: string;
  uploadAndProcessState: FileUploadState | FileProcessingState;
  setUploadAndProcessState: React.Dispatch<
    React.SetStateAction<FileUploadState | FileProcessingState>
  >;
  uploadedFiles: File[];
  setStopProcessingError: React.Dispatch<React.SetStateAction<string | undefined>>;
  setIsStopProcessModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setIsReUploadModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

interface UploadSectionApisReturn {
  /**
   * Method to reset state value and processing api.
   */
  resetStateValueAndApiStatus: () => void;
  /**
   * File uuid that has been uploaded.
   */
  fileUuid: string;
  /**
   * Mutation API to retrieve pre-signed url to upload the excel file.
   */
  presignedUrlMutation: UseCustomMutationReturn<
    PresignedUrlResponse,
    AxiosError,
    PostUploadPresignedUrlRequest,
    unknown
  >;
  /**
   * Query API method to download the error file.
   */
  downloadErrorFile: (
    options?: RefetchOptions
  ) => Promise<QueryObserverResult<DownloadFileResponse, AxiosError<GenericErrorResponse>>>;
  /**
   * Mutation API to re-upload the excel file.
   */
  reUploadMutation: UseCustomMutationReturn<void, AxiosError, PutReUploadExcelFile, unknown>;
  /**
   * Mutation API to stop the excel file processing.
   */
  stopProcessingMutation: UseCustomMutationReturn<
    void,
    AxiosError,
    PutStopFileProcessingRequest,
    unknown
  >;
}

const useUploadSectionApis = ({
  applicationUuid,
  uploadAndProcessState,
  setUploadAndProcessState,
  uploadedFiles,
  setIsReUploadModalOpen,
  setIsStopProcessModalOpen,
  setStopProcessingError,
}: UploadSectionApisInput): UploadSectionApisReturn => {
  const { t } = useTranslation();

  const { refetch: refetchRequestSummary } = useGetRequestSummary({
    uuid: applicationUuid,
    onSuccess: (data) => {
      // Reset when file status changed to resolve issue tat confirmation dialog still shown to user after upload a new file.
      if (data.fileStatus && data.fileStatus !== uploadAndProcessState) {
        resetStateValueAndApiStatus();
      }

      if (data.fileStatus) {
        setUploadAndProcessState(data.fileStatus);
      } else {
        setUploadAndProcessState(FileUploadState.IDLE);
      }
    },
  });

  const startProcessingMutation = usePutStartFileProcessing({
    onSuccess: () => {
      setUploadAndProcessState(FileProcessingState.PROCESSING);
      refetchRequestSummary();
    },
  });

  const stopProcessingMutation = usePutStopFileProcessing({
    onSuccess: () => {
      resetStateValueAndApiStatus();
      refetchRequestSummary();
    },
    onError: (err) => {
      if (err.response?.data.code === '1006') {
        setStopProcessingError(
          t('specialConsideration->>UploadRankingDetail.FileNotInProcessingState')
        );
      } else {
        setStopProcessingError(t('components->>Error.DefaultErrorMsg'));
      }
    },
  });

  const reUploadMutation = usePutReUploadExcelFile({
    onSuccess: () => {
      resetStateValueAndApiStatus();
      refetchRequestSummary();
    },
  });

  const presignedUrlMutation = usePostUploadPresignedUrl({
    onSuccess: (res) => {
      uploadFile(res, uploadedFiles, uploadMutation.mutate);
    },
    onError: () => {
      setUploadAndProcessState(FileUploadState.FAILED);
    },
  });

  const fileUuid = presignedUrlMutation.data?.key || '';
  const uploadMutation = usePutUploadToS3({
    onError: () => {
      setUploadAndProcessState(FileUploadState.FAILED);
    },
  });
  const uploadStatusQuery = useGetDocumentUploadStatus({
    fileKey: fileUuid || '',
    enabled: uploadAndProcessState === FileUploadState.UPLOADING,
    refetchInterval: 5000,
    onSuccess: (data) => {
      if (data.fileStatus === FileUploadState.SUCCESS) {
        startProcessingMutation.mutate({ fileUuid, applicationUuid });
      } else if (
        data.fileStatus === FileUploadState.FAILED ||
        data.fileStatus === FileUploadState.TIMEOUT
      ) {
        setUploadAndProcessState(FileUploadState.FAILED);
      }
    },
  });

  const { refetch: downloadErrorFile } = useGetDownloadErrorFile({
    uuid: applicationUuid,
    onSuccess: (res) => {
      const newWindow = window.open(res.url, '_blank', 'noopener,noreferrer');
      if (newWindow) {
        newWindow.opener = null;
      }
    },
  });

  const resetStateValueAndApiStatus = () => {
    stopProcessingMutation.reset();
    reUploadMutation.reset();
    uploadStatusQuery.remove();
    setStopProcessingError(undefined);
    setIsReUploadModalOpen(false);
    setIsStopProcessModalOpen(false);
  };

  return {
    resetStateValueAndApiStatus,
    downloadErrorFile,
    reUploadMutation,
    stopProcessingMutation,
    fileUuid,
    presignedUrlMutation,
  };
};

export default useUploadSectionApis;
